import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaEdit, FaRegPauseCircle, FaRegPlayCircle } from 'react-icons/fa'; // Import icons
import '../../style/VaccineDetailsPage.css';
import recurrenceOptions from '../../data/recurrenceOptions';
import statusOptions from '../../data/status';
import petVaccines from '../../data/petVaccines';
import { useTranslation } from 'react-i18next'; // Import translation hook
import i18n from '../../i18n'; // Import i18n instance

const VaccineDetailsPage = () => {
    const { id, vaccineId } = useParams(); // Get pet ID and vaccine ID from URL
    const navigate = useNavigate();
    const { t } = useTranslation(); // Translation function
    const [vaccine, setVaccine] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDateInput, setShowDateInput] = useState(false); // State to toggle date input visibility
    const [givenDate, setGivenDate] = useState(''); // State to store the selected date
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchVaccineDetails = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error(t('general.noAuthToken')); // Use translation for error
                }

                const response = await fetch(`${baseUrl}/api/pets/${id}/vaccines/${vaccineId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error(t('vaccine.fetchFailed')); // Use translation for error
                }

                const data = await response.json();
                setVaccine(data);
            } catch (error) {
                setError(error.message);
                console.error(error);
            }
        };

        fetchVaccineDetails();
    }, [id, vaccineId, baseUrl, t]);

    const getRecurrenceLabel = (value) => {
        const option = recurrenceOptions.find((option) => option.value === value);
        return option ? option.label[i18n.language] : value; // Use multilingual labels
    };

    const getStatusLabel = (value) => {
        const option = statusOptions.find((option) => option.value === value);
        return option ? option.label[i18n.language] : value; // Use multilingual labels
    };

    const handleToggleStatus = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const newStatus = vaccine.status === 'active' ? 'pause' : 'activate';
            const response = await fetch(`${baseUrl}/api/pets/${id}/vaccines/${vaccineId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ action: newStatus }),
            });

            if (!response.ok) {
                throw new Error(t('vaccine.updateFailedStatus')); // Use translation for error
            }

            const updatedVaccine = await response.json();
            setVaccine(updatedVaccine.find((v) => v._id === vaccineId)); // Update the vaccine state
        } catch (error) {
            setError(error.message);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsGiven = () => {
        setShowDateInput(true); // Show the date input
    };

    const handleConfirmGivenDate = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('general.noAuthToken')); // Use translation for error
            }

            const response = await fetch(`${baseUrl}/api/pets/${id}/vaccines/${vaccineId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ action: 'given', date: givenDate }),
            });

            if (!response.ok) {
                throw new Error(t('vaccine.markGivenFailed')); // Use translation for error
            }

            const updatedVaccine = await response.json();
            setVaccine(updatedVaccine.find((v) => v._id === vaccineId)); // Update the vaccine state
            setShowDateInput(false); // Hide the date input
            setGivenDate(''); // Reset the date input
        } catch (error) {
            setError(error.message);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancelDateInput = () => {
        setShowDateInput(false); // Hide the date input
        setGivenDate(''); // Reset the date input
    };

    const handleEditClick = () => {
        navigate(`/pet/${id}/vaccine/${vaccineId}/edit`);
    };

    const formatName = (name) => {
        return name.replace(/_/g, ' '); // Replace underscores with spaces
    };

    const formatNotes = (notes) => {
        if (!notes) return t('general.noNotes'); // Use translation for "No notes"
        return notes.replace(/_/g, ' ').replace(/,/g, ' / '); // Replace underscores with spaces and commas with slashes
    };

    if (error) {
        return <div className="error-message text-red-500 text-center">{error}</div>;
    }

    if (!vaccine) {
        return <div className="loading-message text-dafauna-gray text-center">{t('general.loading')}</div>;
    }

    return (
        <div className="vaccine-details-page bg-dafauna-background rounded-xl shadow-md p-8">
            <div className="vaccine-header flex justify-between items-center mb-4">
                <div className="header-left flex items-center gap-4">
                    <h2 className="text-dafauna-darkText text-2xl font-bold">{vaccine.name.map((name) => {
                        const vaccineData = petVaccines.find((v) => v.value === name);
                        return vaccineData ? vaccineData.label[i18n.language] : formatName(name);
                    }).join(' / ')}</h2>
                    <FaEdit
                        className="edit-icon text-dafauna-primary hover:text-dafauna-primaryLight cursor-pointer"
                        onClick={handleEditClick}
                        title={t('vaccine.titleEdit')}
                    />
                </div>
                <div className="header-right">
                    <div
                        onClick={handleToggleStatus}
                        className="status-icon cursor-pointer"
                        title={vaccine.status === 'active' ? t('vaccine.pause') : t('vaccine.activate')}
                    >
                        {vaccine.status === 'active' ? (
                            <FaRegPauseCircle className="pause-icon text-red-500 text-xl" />
                        ) : (
                            <FaRegPlayCircle className="play-icon text-dafauna-primary text-xl" />
                        )}
                    </div>
                </div>
            </div>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.status')}:</strong> {getStatusLabel(vaccine.status) || t('general.na')}</p>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.lastOne')}:</strong> {vaccine.lastGivenDate ? new Date(vaccine.lastGivenDate).toLocaleDateString() : t('general.na')}</p>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.nextOne')}:</strong> {vaccine.nextGivenDate ? new Date(vaccine.nextGivenDate).toLocaleDateString() : t('general.na')}</p>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.end')}:</strong> {vaccine.endDate ? new Date(vaccine.endDate).toLocaleDateString() : t('general.na')}</p>
            <p className="text-dafauna-darkText mb-2"><strong>{t('general.recurrence')}:</strong> {getRecurrenceLabel(vaccine.recurrence) || t('general.na')}</p>

            <div className="actions mt-4">
                {vaccine.status === 'active' && (
                    <>
                        <button
                            onClick={handleMarkAsGiven}
                            className="mark-as-given-btn"
                            disabled={loading}
                        >
                            {loading ? t('general.updating') : t('general.markAsGiven')}
                        </button>
                        {showDateInput && (
                            <div className="date-input-container flex flex-col gap-4 mt-4">
                                <h4 className="text-dafauna-darkText">{t('general.confirmGiven')}</h4>
                                <input
                                    type="date"
                                    value={givenDate}
                                    onChange={(e) => setGivenDate(e.target.value)}
                                    className="form-input border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:border-dafauna-primary"
                                    required
                                />
                                <div className="date-input-actions flex items-center gap-4">
                                    <button
                                        onClick={handleConfirmGivenDate}
                                        className="confirm-date-btn"
                                        disabled={loading || !givenDate}
                                    >
                                        {t('general.confirm')}
                                    </button>
                                    <button
                                        onClick={handleCancelDateInput}
                                        className="cancel-date-btn"
                                        disabled={loading}
                                    >
                                        {t('general.cancel')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            <h3 className="text-dafauna-darkText text-xl font-bold mt-4">{t('general.history')}</h3>
            {vaccine.history && vaccine.history.length > 0 ? (
                <table className="history-table w-full border-collapse mt-4">
                    <thead>
                        <tr>
                            <th className="bg-dafauna-gray text-white">{t('general.date')}</th>
                            <th className="bg-dafauna-primary text-white">{t('general.action')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {vaccine.history
                            .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort by date in descending order
                            .map((entry, index) => (
                                <tr key={index}>
                                    <td className="border border-gray-300 p-2">{new Date(entry.date).toLocaleDateString()}</td>
                                    <td className="border border-gray-300 p-2">{formatNotes(entry.notes)}</td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            ) : (
                <p className="text-dafauna-gray">{t('vaccine.noHistory')}</p>
            )}
        </div>
    );
};

export default VaccineDetailsPage;